import { WeaponStats, CalculationResult } from "../../types/calculations";
import { WeaponMount, Weapon, WeaponRange } from "../../types/weapons";
import { DefensiveSystem, AmmunitionStorage } from "../../types/ship";
import { SPINAL_WEAPON_RANGE_ADJUSTMENTS } from "../../constants/ship";

/**
 * Calculates weapon statistics including costs and weights
 * @param weapons - Array of weapons
 * @param weaponCounts - Weapon counts
 * @returns Calculation result with weapon stats or error
 */
export const calculateWeaponStats = (
  weapons: Array<{ name: string; cost: number; tons: number; power: number }>,
  weaponCounts: Record<string, number>,
): CalculationResult<WeaponStats> => {
  try {
    let totalCost = 0;
    let totalTons = 0;
    let totalPower = 0;

    // Calculate stats for each weapon
    weapons.forEach((weapon) => {
      const count = weaponCounts[weapon.name] || 1;
      totalCost += weapon.cost * count;
      totalTons += weapon.tons * count;
      totalPower += weapon.power * count;
    });

    return {
      success: true,
      data: {
        cost: totalCost,
        weight: totalTons,
        power: totalPower,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: {
        type: "CALCULATION_ERROR",
        message: `Failed to calculate weapon stats: ${error instanceof Error ? error.message : "Unknown error"}`,
      },
    };
  }
};

/**
 * Calculates spinal weapon statistics
 * @param weapon - The spinal weapon
 * @param sizeMultiple - Size multiple for the weapon
 * @returns Spinal weapon stats
 */
export const calculateSpinalWeaponStats = (
  weapon: any, // Accept WeaponType from constants
  sizeMultiple: number = 1,
): {
  power: number;
  damage: string;
  cost: number;
  tons: number;
  hardpoints: number;
} => {
  const basePower = weapon.power * sizeMultiple;
  const baseDamage = weapon.damage;
  const baseCost = weapon.cost * sizeMultiple;
  const baseTons = weapon.tons * sizeMultiple;
  
  // Spinal weapons use hardpoints equal to their tonnage divided by 100, rounding up
  const baseHardpoints = Math.ceil(baseTons / 100);

  return {
    power: basePower,
    damage: baseDamage.toString(),
    cost: baseCost,
    tons: baseTons,
    hardpoints: baseHardpoints,
  };
};

/**
 * Calculates spinal weapon range modifier based on attacking ship size
 * @param shipSize - Attacking ship size in tons
 * @param range - Weapon range
 * @returns Range modifier
 */
export const calculateSpinalWeaponRangeModifier = (
  shipSize: number,
  range: WeaponRange,
): number => {
  // Spinal weapons incur negative DMs that vary by both range and attacking ship size
  const validRanges = [
    "Adjacent",
    "Close", 
    "Short",
    "Medium",
    "Long",
    "Very Long",
    "Distant",
  ];
  
  if (validRanges.includes(range)) {
    // @ts-expect-error: TS can't narrow string literal union for object key
    const rangeAdjustments = SPINAL_WEAPON_RANGE_ADJUSTMENTS[range];
    
    if (Array.isArray(rangeAdjustments)) {
      // Determine ship size category for range adjustments
      // Format: [<10k tons, 10k-50k tons, 50k-250k tons, >250k tons]
      let sizeIndex = 0;
      if (shipSize >= 10000 && shipSize < 50000) {
        sizeIndex = 1;
      } else if (shipSize >= 50000 && shipSize < 250000) {
        sizeIndex = 2;
      } else if (shipSize >= 250000) {
        sizeIndex = 3;
      }
      
      return rangeAdjustments[sizeIndex] || 0;
    }
    
    return 0;
  }
  return 0;
};

/**
 * Calculates spinal weapon target modifier based on Traveller rules
 * @param targetSize - Target size in tons
 * @returns Target modifier
 */
export const calculateSpinalWeaponTargetModifier = (
  targetSize: number,
): number => {
  // Traveller rules: All spinal weapons suffer DM-4 when attacking targets of 10,000 tons or less 
  // and DM-8 when attacking targets of 5,000 tons or less. Spinal weapons cannot attack targets 
  // of less than 2,000 tons unless they are stationary or are caught in the blast by accident.
  
  if (targetSize < 2000) {
    return -8; // Cannot attack targets under 2000 tons (unless stationary)
  } else if (targetSize <= 5000) {
    return -8; // DM-8 for targets 5000 tons or less
  } else if (targetSize <= 10000) {
    return -4; // DM-4 for targets 10000 tons or less
  } else {
    return 0; // No penalty for targets over 10000 tons
  }
};

/**
 * Calculates bay weapon target modifier based on Traveller rules
 * @param targetSize - Target size in tons
 * @param weaponType - Type of weapon (to check if missile/torpedo)
 * @returns Target modifier
 */
export const calculateBayWeaponTargetModifier = (
  targetSize: number,
  weaponType?: string,
): number => {
  // Traveller rules: All bay weapons suffer DM-2 when attacking targets of 2,000 tons or less 
  // and DM-4 when attacking targets of 100 tons or less. Missile and torpedo salvoes do not use these modifiers.
  
  // Check if weapon is missile or torpedo (exempt from penalties)
  if (weaponType && (weaponType.includes("missile") || weaponType.includes("torpedo"))) {
    return 0; // No penalty for missile and torpedo salvoes
  }
  
  if (targetSize <= 100) {
    return -4; // DM-4 for targets 100 tons or less
  } else if (targetSize <= 2000) {
    return -2; // DM-2 for targets 2000 tons or less
  } else {
    return 0; // No penalty for targets over 2000 tons
  }
};

/**
 * Validates spinal weapon installation according to Traveller rules
 * @param weapon - The spinal weapon
 * @param shipSize - The size of the ship in tons
 * @param sizeMultiple - Size multiple for the weapon
 * @param weightOverrideActive - If true, skips weight/tonnage validation
 * @returns Array of validation errors
 */
export const validateSpinalWeaponInstallation = (
  weapon: any, // Accept WeaponType from constants
  shipSize: number,
  sizeMultiple: number = 1,
  weightOverrideActive: boolean = false,
): string[] => {
  const errors: string[] = [];
  
  if (!weapon.baseSize) {
    errors.push("Spinal weapon must have a base size defined");
    return errors;
  }
  
  const weaponTons = weapon.baseSize * sizeMultiple;
  
  if (!weightOverrideActive) {
    // Rule: A spinal mount cannot exceed a tonnage equal to half that of the ship carrying it
    const maxAllowedTons = shipSize / 2;
    if (weaponTons > maxAllowedTons) {
      errors.push(
        `Spinal weapon (${weaponTons} tons) cannot exceed half the ship's tonnage (${maxAllowedTons} tons)`
      );
    }
    // Check if weapon exceeds its maximum size
    if (weapon.maxSize && weaponTons > weapon.maxSize) {
      errors.push(
        `Spinal weapon (${weaponTons} tons) exceeds maximum allowed size (${weapon.maxSize} tons)`
      );
    }
  }
  
  return errors;
};

/**
 * Calculates missile effects
 * @param missileType - Missile weapon type
 * @param effect - Effect level
 * @returns Missile effects
 */
export const calculateMissileEffect = (
  missileType: Weapon,
  effect: number,
): {
  powerReduction?: number;
  roundsAffected?: number;
  jumpCheckPenalty?: number;
} => {
  const effects: Record<
    string,
    {
      powerReduction: number;
      roundsAffected: number;
      jumpCheckPenalty: number;
    }
  > = {
    Standard: { powerReduction: 0, roundsAffected: 1, jumpCheckPenalty: 0 },
    Nuclear: { powerReduction: 0, roundsAffected: 1, jumpCheckPenalty: 0 },
    Plasma: { powerReduction: 0, roundsAffected: 1, jumpCheckPenalty: 0 },
    EMP: { powerReduction: 0, roundsAffected: 1, jumpCheckPenalty: 0 },
  };

  return (
    effects[missileType.name] || {
      powerReduction: 0,
      roundsAffected: 1,
      jumpCheckPenalty: 0,
    }
  );
};

/**
 * Calculates missile damage
 * @param missileType - Missile weapon type
 * @param warheadMultiplier - Warhead multiplier
 * @returns Damage string
 */
export const calculateMissileDamage = (
  missileType: Weapon,
  warheadMultiplier: number = 1,
): string => {
  const baseDamage = missileType.damage;
  // Apply warhead multiplier logic here
  return baseDamage.toString();
};

/**
 * Calculates missile point defense penalty
 * @param missileType - Missile weapon type
 * @returns Point defense penalty
 */
export const calculateMissilePointDefensePenalty = (
  missileType: Weapon,
): number => {
  const penalties: Record<string, number> = {
    Standard: 0,
    Nuclear: -2,
    Plasma: -1,
    EMP: 0,
  };

  return penalties[missileType.name] || 0;
};

/**
 * Calculates missile targets
 * @param missileType - Missile weapon type
 * @returns Number of targets
 */
export const calculateMissileTargets = (missileType: Weapon): number => {
  const targets: Record<string, number> = {
    Standard: 1,
    Nuclear: 1,
    Plasma: 1,
    EMP: 1,
  };

  return targets[missileType.name] || 1;
};

/**
 * Calculates sandcaster effects
 * @param canisterType - Sandcaster canister type
 * @returns Sandcaster effects
 */
export const calculateSandcasterEffect = (
  canisterType: Weapon,
): {
  electronicsPenalty?: number;
  sandProtectionReduction?: number;
  groundScaleDamage?: string;
} => {
  const effects: Record<
    string,
    {
      electronicsPenalty: number;
      sandProtectionReduction: number;
      groundScaleDamage: string;
    }
  > = {
    Standard: {
      electronicsPenalty: 0,
      sandProtectionReduction: 0,
      groundScaleDamage: "0",
    },
    Nuclear: {
      electronicsPenalty: -2,
      sandProtectionReduction: 0,
      groundScaleDamage: "1D",
    },
    Plasma: {
      electronicsPenalty: -1,
      sandProtectionReduction: 0,
      groundScaleDamage: "0",
    },
    EMP: {
      electronicsPenalty: -3,
      sandProtectionReduction: 0,
      groundScaleDamage: "0",
    },
  };

  return (
    effects[canisterType.name] || {
      electronicsPenalty: 0,
      sandProtectionReduction: 0,
      groundScaleDamage: "0",
    }
  );
};

/**
 * Calculates sandcaster damage
 * @param canisterType - Sandcaster canister type
 * @returns Damage string
 */
export const calculateSandcasterDamage = (canisterType: Weapon): string => {
  const damages: Record<string, string> = {
    Standard: "0",
    Nuclear: "1D",
    Plasma: "0",
    EMP: "0",
  };

  return damages[canisterType.name] || "0";
};

/**
 * Calculates torpedo effects
 * @param torpedoType - Torpedo weapon type
 * @param targetSize - Target size in tons
 * @returns Torpedo effects
 */
export const calculateTorpedoEffect = (
  torpedoType: Weapon,
  targetSize: number,
): {
  pointDefenseEffectReduction?: number;
  smallTargetPenalty?: number;
  electronicWarfareBonus?: number;
  electronicWarfareRequirement?: boolean;
  standoffAttack?: boolean;
  warheadMultiplier?: number;
  powerReduction?: number;
  roundsAffected?: number;
} => {
  const effects: Record<string, any> = {
    Standard: {
      pointDefenseEffectReduction: 0,
      smallTargetPenalty: targetSize <= 1000 ? -2 : 0,
      electronicWarfareBonus: 0,
      electronicWarfareRequirement: false,
      standoffAttack: false,
      warheadMultiplier: 1,
      powerReduction: 0,
      roundsAffected: 1,
    },
  };

  return effects[torpedoType.name] || effects["Standard"];
};

/**
 * Calculates torpedo damage
 * @param torpedoType - Torpedo weapon type
 * @param effect - Effect level
 * @returns Damage string
 */
export const calculateTorpedoDamage = (
  torpedoType: Weapon,
  effect: number,
): string => {
  const baseDamage = torpedoType.damage;
  // Apply effect multiplier logic here
  return baseDamage.toString();
};

/**
 * Calculates ammo capacity
 * @param weaponType - Weapon type
 * @param tons - Ammo tonnage
 * @returns Ammo capacity and cost
 */
export const calculateAmmoCapacity = (
  weaponType: Weapon,
  tons: number,
): { capacity: number; cost: number } => {
  const baseCapacity = Math.floor(tons / 0.1); // 0.1 tons per round
  const cost = tons * 1000; // 1000 credits per ton

  return {
    capacity: baseCapacity,
    cost: cost,
  };
};

/**
 * Calculates point defense effect
 * @param weaponType - Point defense weapon type
 * @param targetThrust - Target thrust rating
 * @returns Point defense effect
 */
export const calculatePointDefenseEffect = (
  weaponType: Weapon,
  targetThrust: number,
): {
  intercept: string;
  thrustPenalty: number;
} => {
  const baseIntercept = weaponType.damage;
  const thrustPenalty = Math.min(targetThrust, 6);

  return {
    intercept: baseIntercept.toString(),
    thrustPenalty: thrustPenalty,
  };
};

/**
 * Calculates point defense effectiveness
 * @param weaponType - Point defense weapon type
 * @param targetThrust - Target thrust rating
 * @param effect - Effect level
 * @returns Effectiveness percentage
 */
export const calculatePointDefenseEffectiveness = (
  weaponType: Weapon,
  targetThrust: number,
  effect: number,
): number => {
  const baseEffectiveness = 50; // Base 50% chance
  const thrustModifier = Math.min(targetThrust, 6) * 5; // +5% per thrust level
  const effectModifier = effect * 10; // +10% per effect level

  return Math.min(baseEffectiveness + thrustModifier + effectModifier, 95);
};

/**
 * Calculates total cost and weight for all weapon mounts, including weapons and ammo.
 * @param weaponMounts - Array of weapon mounts
 * @returns { cost: number, weight: number }
 */
export function calculateWeaponMountsTotals(weaponMounts: WeaponMount[]): {
  cost: number;
  weight: number;
  power: number;
} {
  let totalCost = 0;
  let totalWeight = 0;
  let totalPower = 0;
  if (!weaponMounts) return { cost: 0, weight: 0, power: 0 };
  weaponMounts.forEach((mount) => {
    // Mount cost/weight/power
    totalCost += mount.cost || 0;
    totalWeight += mount.tons || 0;
    totalPower += mount.power || 0;
    // Weapons in mount
    if (mount.weapons) {
      mount.weapons.forEach((weapon: Weapon) => {
        totalCost += weapon.cost || 0;
        totalWeight += weapon.tons || 0;
        totalPower += weapon.power || 0;
        // Ammo for weapon (type guard for extraReloads and ammo)
        if (
          weapon.ammo &&
          typeof weapon.extraReloads === "number" &&
          weapon.extraReloads > 0 &&
          typeof weapon.ammo.costPerReload === "number" &&
          typeof weapon.ammo.tonsPerReload === "number"
        ) {
          totalCost += weapon.ammo.costPerReload * weapon.extraReloads;
          totalWeight += weapon.ammo.tonsPerReload * weapon.extraReloads;
        }
      });
    }
  });
  return { cost: totalCost, weight: totalWeight, power: totalPower };
}

/**
 * Calculates total cost and weight for all defensive systems.
 * @param defensiveSystems - Array of defensive systems
 * @returns { cost: number, weight: number }
 */
export function calculateDefensiveSystemsTotals(
  defensiveSystems: DefensiveSystem[],
): { cost: number; weight: number; power: number } {
  return defensiveSystems.reduce(
    (totals, system) => {
      const quantity = system.quantity || 1;
      return {
        cost: totals.cost + ((system.cost || 0) * quantity),
        weight: totals.weight + ((system.weight || 0) * quantity),
        power: totals.power + ((system.power || 0) * quantity),
      };
    },
    { cost: 0, weight: 0, power: 0 },
  );
}

export function calculateAmmunitionStorageTotals(
  ammunitionStorage: AmmunitionStorage[],
): { cost: number; weight: number } {
  return ammunitionStorage.reduce(
    (totals, ammo) => ({
      cost: totals.cost + ammo.cost * ammo.quantity,
      weight: totals.weight + ammo.tonsPerReload * ammo.quantity,
    }),
    { cost: 0, weight: 0 },
  );
}

// export const calculateScreenEffect = (
//   screenType: ScreenWeapon,
//   effect: number,
//   isDestructiveWeapon: boolean = false
// ): { damageReduction: string; radiationRemoval: number } => {
//   // Implementation here
// };
